import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Hexagon, Lock, ArrowRight, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tooShort = pwd && pwd.length < 8;
  const mismatch = pwd2 && pwd !== pwd2;

  const submit = async (e) => {
    e.preventDefault();
    if (tooShort) return toast.error("Password must be at least 8 characters");
    if (mismatch) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password updated — you're signed in.");
      // Backend sets the auth cookie + returns the user. Refresh auth context if available.
      try { await refreshUser?.(); } catch { /* fall through */ }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600 mb-3"><AlertTriangle size={22} /></div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Reset link is invalid</h1>
          <p className="text-sm text-slate-500 mb-5">The reset link is missing its token. Please request a fresh one.</p>
          <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700">
            Request a new link <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-xl bg-orange-500 grid place-items-center text-white">
            <Hexagon size={18} />
          </div>
          <div>
            <div className="text-base font-bold text-slate-900 tracking-tight">LeadHub</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Set new password</div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="reset-password-form">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Choose a new password</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Minimum 8 characters. After saving, you&apos;ll be signed in automatically.</p>

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">New password</label>
          <div className="relative mb-1">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="reset-password-new"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="At least 8 characters"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
            />
          </div>
          {tooShort && <div className="text-xs text-rose-600 mb-3">Password must be at least 8 characters</div>}
          {!tooShort && <div className="mb-3" />}

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Confirm password</label>
          <div className="relative mb-1">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="reset-password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              placeholder="Re-enter password"
              className="w-full pl-9 pr-3 py-2.5 rounded-md border border-slate-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none text-sm"
            />
          </div>
          {mismatch && <div className="text-xs text-rose-600 mb-3">Passwords don&apos;t match</div>}
          {!mismatch && <div className="mb-5" />}

          <button
            data-testid="reset-password-submit"
            disabled={submitting || tooShort || mismatch || !pwd || !pwd2}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-md transition-colors flex items-center justify-center gap-1.5 shadow-sm"
          >
            {submitting ? "Saving…" : "Save and sign in"} {!submitting && <ArrowRight size={14} />}
          </button>
        </form>
      </div>
    </div>
  );
}
